import { useEffect, useMemo, useRef } from 'react';
import { Sparkles } from 'lucide-react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useCommandPalette } from '@dashboard/components/CommandPalette';
import { AI_EDITOR_PATH } from '@dashboard/context/AIContext/aiEditorRoute';

import { getDesignSections } from '../scenes/PageDesign/theming/getDesignSections';

export function buildPageCommandEntries({ base, tabs, screens, designSections }) {
  const aiEditorEntry = {
    id: 'page-ai-editor',
    label: 'AI editor',
    icon: Sparkles,
    path: `${base}/customize${AI_EDITOR_PATH}`,
    keywords: ['AI', 'Chat'],
  };

  const screenEntries = screens.map(screen => ({
    id: `page-${screen.path}`,
    label: screen.label,
    icon: screen.icon,
    path: `${base}/${screen.path}`,
    hint: 'Overview',
    keywords: ['Overview'],
  }));

  const sectionEntries = tabs.flatMap(tab => {
    const sections =
      tab.path === 'design'
        ? [
            { label: tab.label, url: `${base}/customize/design/theme` },
            ...designSections.map(section => ({
              label: section.label,
              url: `${base}/customize/design/theme#${section.id}`,
            })),
          ]
        : tab.flat && !tab.sections.length
          ? [{ label: tab.label, url: `${base}/customize/${tab.path}` }]
          : tab.sections.map(section => ({
              label: section.label,
              url: `${base}/customize/${tab.path}/${section.path}`,
            }));

    return sections.map(section => ({
      id: `page-${section.url}`,
      label: section.label,
      icon: tab.icon,
      path: section.url,
      hint: tab.label,
      keywords: [tab.label],
    }));
  });

  return [aiEditorEntry, ...screenEntries, ...sectionEntries];
}

export default function usePageCommandEntries({ base, tabs, screens }) {
  const { seller } = useAuth();
  const { setPageEntries } = useCommandPalette();

  const entries = useMemo(
    () =>
      buildPageCommandEntries({
        base,
        tabs,
        screens,
        designSections: getDesignSections({ seller }).filter(section => section.type !== 'heading'),
      }),
    [base, tabs, screens, seller],
  );

  // Compared by content: a new seller or page object with the same sections must not
  // push the list again, or the palette's update re-renders this page in a loop.
  const signature = entries.map(entry => `${entry.path}\t${entry.label}`).join('\n');
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  useEffect(() => {
    setPageEntries(entriesRef.current);
  }, [signature, setPageEntries]);

  useEffect(() => () => setPageEntries([]), [setPageEntries]);
}
